import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ThemeContext } from '../hooks/ThemeContext';
import { UserContext } from '../hooks/UserContext';
import { api } from '../services/baseUrl';
import { User, Send, ArrowLeft, Mail } from 'lucide-react';

const ContactCreator = () => {
  const { theme } = useContext(ThemeContext);
  const { isAuthenticated, user } = useContext(UserContext);
  const { creatorId } = useParams();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [creator, setCreator] = useState(null);
  const [creators, setCreators] = useState([]);
  const [selectedCreatorId, setSelectedCreatorId] = useState(creatorId || '');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingCreators, setLoadingCreators] = useState(true);

  useEffect(() => {
    fetchCreators();
  }, []);

  useEffect(() => {
    if (selectedCreatorId) {
      const found = creators.find(c => c.id_user === parseInt(selectedCreatorId));
      setCreator(found);
    }
  }, [selectedCreatorId, creators]);

  const fetchCreators = async () => {
    try {
      setLoadingCreators(true);
      const response = await api.get('/creators');
      const creatorsList = response.data?.data || response.data || [];
      setCreators(creatorsList);
      
      if (creatorId && creatorsList.length > 0) {
        const found = creatorsList.find(c => c.id_user === parseInt(creatorId));
        if (found) {
          setCreator(found);
          setSelectedCreatorId(creatorId);
        }
      }
    } catch (error) {
      console.error('Erreur chargement créateurs:', error);
      toast.error('Impossible de charger les créateurs');
    } finally {
      setLoadingCreators(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      toast.error('Vous devez être connecté pour contacter un créateur');
      navigate('/login');
      return;
    }

    if (!selectedCreatorId) {
      toast.error('Veuillez sélectionner un créateur');
      return;
    }

    if (!message.trim()) {
      toast.error('Le message ne peut pas être vide');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/conversations/send', {
        receiver_id: parseInt(selectedCreatorId),
        content: message.trim(),
      });

      if (response.data) {
        toast.success('Message envoyé au créateur avec succès');
        setMessage('');
        // Rediriger vers l'espace messages
        setTimeout(() => {
          navigate('/espace-client?tab=messages');
        }, 1500);
      }
    } catch (error) {
      console.error('Erreur envoi message:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'envoi du message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className={`flex items-center gap-2 mb-4 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          
          <h1 className="text-3xl font-bold mb-2">Contacter un Créateur</h1>
          <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Discutez directement avec un créateur pour vos projets personnalisés
          </p>
        </div>

        {/* Avertissement si non connecté */}
        {!isAuthenticated && (
          <div className={`mb-6 p-4 rounded-lg ${isDark ? 'bg-orange-500/10 border border-orange-500/20 text-orange-400' : 'bg-orange-50 border border-orange-200 text-orange-700'}`}>
            <p className="font-medium">⚠️ Connexion requise</p>
            <p className="text-sm mt-1">Vous devez être connecté pour contacter un créateur.</p>
          </div>
        )}

        {/* Formulaire */}
        <div className={`rounded-xl border p-8 ${isDark ? 'bg-[#0b0b0c] border-white/10' : 'bg-white border-gray-200'}`}>
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Sélection du créateur */}
            <div>
              <label className={`block mb-2 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Sélectionner un créateur
              </label>
              
              {loadingCreators ? (
                <div className={`p-4 rounded-lg text-center ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                  Chargement des créateurs...
                </div>
              ) : creators.length === 0 ? (
                <div className={`p-4 rounded-lg text-center ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                  Aucun créateur disponible
                </div>
              ) : (
                <select
                  value={selectedCreatorId}
                  onChange={(e) => setSelectedCreatorId(e.target.value)}
                  required
                  className={`w-full px-4 py-3 rounded-lg border transition-colors ${
                    isDark
                      ? 'bg-white/5 border-white/15 text-white focus:border-rose-500 focus:bg-white/10'
                      : 'bg-white border-gray-200 text-gray-800 focus:border-rose-600'
                  } focus:outline-none`}
                >
                  <option value="">Choisissez un créateur</option>
                  {creators.map((c) => (
                    <option key={c.id_user} value={c.id_user}>
                      {c.nickname || `${c.firstname || ''} ${c.lastname || ''}`.trim()} 
                      {c.specialties ? ` - ${c.specialties}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Info créateur sélectionné */}
            {creator && (
              <div className={`p-4 rounded-lg ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDark ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-100 text-rose-600'}`}>
                    <User className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-semibold">
                      {creator.nickname || `${creator.firstname || ''} ${creator.lastname || ''}`.trim()}
                    </div>
                    {creator.specialties && (
                      <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {creator.specialties}
                      </div>
                    )}
                  </div>
                </div>
                {creator.bio && (
                  <p className={`mt-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {creator.bio}
                  </p>
                )}
              </div>
            )}

            {/* Message */}
            <div>
              <label className={`block mb-2 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Votre message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={8}
                placeholder="Décrivez votre projet, vos besoins, vos questions..."
                className={`w-full px-4 py-3 rounded-lg border transition-colors resize-none ${
                  isDark
                    ? 'bg-white/5 border-white/15 text-white placeholder-white/50 focus:border-rose-500 focus:bg-white/10'
                    : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-rose-600'
                } focus:outline-none`}
              />
              <p className={`text-sm mt-2 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                💡 Soyez précis : budget, délais, style souhaité, exemples...
              </p>
            </div>

            {/* Bouton submit */}
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading || !isAuthenticated || !selectedCreatorId}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                  loading || !isAuthenticated || !selectedCreatorId
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-rose-600 hover:bg-rose-700 text-white'
                }`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Envoyer le message
                  </>
                )}
              </button>
            </div>

            {/* Info surveillance admin */}
            <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-500/10 border border-blue-500/20 text-blue-400' : 'bg-blue-50 border border-blue-200 text-blue-700'}`}>
              <p className="text-sm flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Les conversations avec les créateurs sont surveillées par notre équipe pour garantir la qualité du service.
              </p>
            </div>
          </form>
        </div>

        {/* Info complémentaire */}
        <div className={`mt-6 p-6 rounded-xl ${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 border border-white/10' : 'bg-gradient-to-br from-rose-50 via-purple-50 to-blue-50 border border-gray-200'}`}>
          <h3 className="font-semibold mb-3">Pourquoi contacter directement un créateur ?</h3>
          <ul className={`space-y-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <li>✓ Projets personnalisés sur mesure</li>
            <li>✓ Discussion directe de vos besoins</li>
            <li>✓ Devis adapté à votre budget</li>
            <li>✓ Suivi personnalisé de votre projet</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ContactCreator;
